/**
 * Document Attributes Accessor Functions
 *
 * This module provides a type-safe API for reading and writing document-level
 * attributes stored in the DocumentAttributesPlugin.
 *
 * Usage:
 *   // Reading
 *   const labware = getLabware(editor.state);
 *
 *   // Writing
 *   setLabware(editor, newLabwareArray);
 *
 *   // JSON Export
 *   const json = getDocumentJSON(editor);
 */

import type { Editor, JSONContent } from "@tiptap/core";
import type { EditorState } from "@tiptap/pm/state";

import { docAttrPluginKey } from "@/plugins/DocumentAttributesPlugin";
import {
  type DocumentAttributesState,
  type DocumentAttributesAction,
  type SelectOptionsState,
  type LabwareObject,
  type RunConfiguration,
  DEFAULT_DOCUMENT_ATTRIBUTES,
} from "@/plugins/documentAttributes.types";

// ============================================================================
// READING (from plugin state)
// ============================================================================

/**
 * Get the complete document attributes state
 */
export function getDocumentAttributes(
  state: EditorState
): DocumentAttributesState {
  const pluginState = docAttrPluginKey.getState(state);
  return pluginState ?? DEFAULT_DOCUMENT_ATTRIBUTES;
}

/**
 * Get selectOptions (contains labware array)
 */
export function getSelectOptions(state: EditorState): SelectOptionsState {
  return getDocumentAttributes(state).selectOptions;
}

/**
 * Get labware array directly
 * Convenience function equivalent to getSelectOptions(state).labware
 */
export function getLabware(state: EditorState): LabwareObject[] {
  return getSelectOptions(state).labware ?? [];
}

/**
 * Get a specific labware by ID
 * Returns undefined if not found
 */
export function getLabwareById(
  state: EditorState,
  id: string
): LabwareObject | undefined {
  return getLabware(state).find((lw) => lw.id === id);
}

/**
 * Get run configuration
 */
export function getRunConfiguration(
  state: EditorState
): RunConfiguration | null {
  return getDocumentAttributes(state).runConfiguration;
}

// ============================================================================
// WRITING (dispatches setMeta transactions)
// ============================================================================

/**
 * Set the complete selectOptions object
 */
export function setSelectOptions(
  editor: Editor,
  options: SelectOptionsState
): void {
  const action: DocumentAttributesAction = {
    type: "SET_SELECT_OPTIONS",
    payload: options,
  };
  editor.view.dispatch(editor.state.tr.setMeta(docAttrPluginKey, action));
}

/**
 * Replace the entire labware array
 */
export function setLabware(editor: Editor, labware: LabwareObject[]): void {
  const action: DocumentAttributesAction = {
    type: "SET_LABWARE",
    payload: labware,
  };
  editor.view.dispatch(editor.state.tr.setMeta(docAttrPluginKey, action));
}

/**
 * Add a new labware item
 * If labware with same ID exists, it will be updated instead
 */
export function addLabware(editor: Editor, labware: LabwareObject): void {
  const currentLabware = getLabware(editor.state);
  const existingIndex = currentLabware.findIndex((lw) => lw.id === labware.id);

  if (existingIndex >= 0) {
    // Update existing
    updateLabware(editor, labware.id, labware);
  } else {
    // Add new
    setLabware(editor, [...currentLabware, labware]);
  }
}

/**
 * Add multiple labware items at once
 * If labware with same ID exists, it will be updated instead
 */
export function addLabwareItems(
  editor: Editor,
  labwareItems: LabwareObject[]
): void {
  const currentLabware = getLabware(editor.state);
  const currentLabwareMap = new Map(currentLabware.map((lw) => [lw.id, lw]));

  // Merge: update existing, add new
  for (const item of labwareItems) {
    currentLabwareMap.set(item.id, {
      ...(currentLabwareMap.get(item.id) ?? {}),
      ...item,
    });
  }

  setLabware(editor, Array.from(currentLabwareMap.values()));
}

/**
 * Update an existing labware item by ID
 * Merges updates with existing labware properties
 */
export function updateLabware(
  editor: Editor,
  id: string,
  updates: Partial<LabwareObject>
): void {
  const action: DocumentAttributesAction = {
    type: "UPDATE_LABWARE",
    payload: { id, updates },
  };
  editor.view.dispatch(editor.state.tr.setMeta(docAttrPluginKey, action));
}

/**
 * Delete a labware item by ID
 */
export function deleteLabware(editor: Editor, id: string): void {
  const action: DocumentAttributesAction = {
    type: "DELETE_LABWARE",
    payload: { id },
  };
  editor.view.dispatch(editor.state.tr.setMeta(docAttrPluginKey, action));
}

/**
 * Set run configuration
 */
export function setRunConfiguration(
  editor: Editor,
  config: RunConfiguration | null
): void {
  const action: DocumentAttributesAction = {
    type: "SET_RUN_CONFIGURATION",
    payload: config,
  };
  editor.view.dispatch(editor.state.tr.setMeta(docAttrPluginKey, action));
}

// ============================================================================
// JSON EXPORT
// ============================================================================

/**
 * Get document JSON with plugin state merged into attrs
 *
 * This is a drop-in replacement for editor.getJSON() that ensures
 * document attributes from plugin state are included in the output.
 *
 * @example
 * // Before (missing plugin state attributes):
 * const json = editor.getJSON();
 *
 * // After (includes all document attributes):
 * const json = getDocumentJSON(editor);
 */
export function getDocumentJSON(editor: Editor): JSONContent {
  const baseJSON = editor.getJSON();
  const pluginState = getDocumentAttributes(editor.state);

  // Merge plugin state into attrs
  return {
    ...baseJSON,
    attrs: {
      ...(baseJSON.attrs || {}),
      selectOptions: pluginState.selectOptions,
      runConfiguration: pluginState.runConfiguration,
    },
  };
}

// ============================================================================
// TRANSACTION HELPERS (for batch operations)
// ============================================================================

/**
 * Create a transaction action for setting labware
 * Useful when you need to batch multiple changes in a single transaction
 *
 * @example
 * editor.chain()
 *   .command(({ tr }) => {
 *     tr.setMeta(docAttrPluginKey, createSetLabwareAction(newLabware));
 *     // ... other transaction operations
 *     return true;
 *   })
 *   .run();
 */
export function createSetLabwareAction(
  labware: LabwareObject[]
): DocumentAttributesAction {
  return {
    type: "SET_LABWARE",
    payload: labware,
  };
}

export function createUpdateLabwareAction(
  id: string,
  updates: Partial<LabwareObject>
): DocumentAttributesAction {
  return {
    type: "UPDATE_LABWARE",
    payload: { id, updates },
  };
}

export function createDeleteLabwareAction(id: string): DocumentAttributesAction {
  return {
    type: "DELETE_LABWARE",
    payload: { id },
  };
}

export function createSetRunConfigurationAction(
  config: RunConfiguration | null
): DocumentAttributesAction {
  return {
    type: "SET_RUN_CONFIGURATION",
    payload: config,
  };
}

// Re-export plugin key for advanced usage
export { docAttrPluginKey };

// Re-export types for convenience
export type {
  DocumentAttributesState,
  DocumentAttributesAction,
  SelectOptionsState,
  LabwareObject,
  RunConfiguration,
};
